import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import { Close } from "@mui/icons-material";

import { RootState } from "../../../store/Index";
import {
  IEditRateDialogProps,
  IRoleSkillTool,
  RATE_CARD_CURRENCIES,
  RATE_CARD_RATE_TYPES,
  RateCardRateType,
} from "../types/rate-card";
import apiService from "../../../service/Api";
import CustomTextField from "../../../components/common/Textfield";

export const EditRateDialog: React.FC<IEditRateDialogProps> = ({
  formData,
  formLoading,
  handleEditRate,
  handleInputChange,
  handleRolesEdit,
  open,
  rateType,
  resetForm,
  selectedRateCard,
  selectedRoles, // this version of selectedRoles is not correct, this component should get selectedRoles from the one defined in the database for that rate card 
  setDialogOpen,
  setSelectedRoles,
  setRateType,
}) => {
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [roleVal, setRoleVal] = useState<string | null>("");
  const [rolesLoading, setRolesLoading] = useState(false);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const roles = await apiService.get<{ results: IRoleSkillTool[] }>(
        "/api/v1/crews/roles/",
        { page_size: 20 }
      );

      const mapped = roles.data.results.map(rl => (
        `${rl.id} - ${rl.name}`
      ));

      setRoleOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchRoles();
    }
  }, [fetchRoles, open]);

  useEffect(() => {
    if (selectedRateCard?.role?.id && selectedRateCard.role?.name) {
      setRoleVal(`${selectedRateCard.role.id} - ${selectedRateCard.role.name}`);
      return;
    }

    setRoleVal("");
  }, [selectedRateCard]);

  if (!formData) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        setDialogOpen(false);
        setSelectedRoles(undefined);
        setRateType("fixed");
        resetForm();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Editing {selectedRateCard?.role?.name ?? formData.role.name} Rate Card
        <IconButton
          onClick={() => {
            setDialogOpen(false);
            setSelectedRoles(undefined);
            setRateType("fixed");
            resetForm();
          }}
          sx={{ position: "absolute", right: 8, top: 8 }}
          disabled={formLoading}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              value={roleVal}
              onChange={(event: React.SyntheticEvent, newValue: string | null) => {
                setRoleVal(newValue);
                handleRolesEdit(newValue);
              }}
              loading={rolesLoading}
              inputValue={selectedRoles ?? ""}
              onInputChange={(event, newInputValue) => {
                handleRolesEdit(newInputValue);
              }}
              options={roleOptions}
              sx={{ width: '100%', borderRadius: 2 }}
              renderInput={(params) => 
                <CustomTextField
                  {...params}
                  placeholder="Select role..."
                  variant="outlined"
                  label={'Roles'}
                  helperText="Type 3 letters to search"
                />
              }
            />
          </Grid>

          <Grid size={12}>
            <Box sx={{ pb: 2 }}>
              <Typography
                sx={{
                  borderBottom: '1px solid',
                  mb: 2,
                  borderColor: 'divider'
                }}
                variant={'subtitle1'}>
                  {'Rate Details'}
              </Typography>

              <Box sx={{ display: "none" }}>
                <FormControl fullWidth>
                  <InputLabel id="edit-rate-type-label">Rate Type</InputLabel>
                  <Select
                    labelId="edit-rate-type-label"
                    label="Rate Type"
                    value={rateType}
                    onChange={(event) =>
                      setRateType(event.target.value as RateCardRateType)
                    }
                  >
                    {RATE_CARD_RATE_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <CustomTextField
                name="fixed_rate"
                label="Rate Amount"
                placeholder="Enter the rate amount"
                fullWidth
                value={formData.fixed_rate}
                onChange={(e) => handleInputChange("fixed_rate", e.target.value)}
                disabled={formLoading}
              />
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="edit-rate-currency-label">Currency</InputLabel>
              <Select
                labelId="edit-rate-currency-label"
                label="Currency"
                value={formData.currency}
                onChange={(event) => handleInputChange("currency", event.target.value)}
              >
                {RATE_CARD_CURRENCIES.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomTextField
              name="effective_date"
              label="Effective Date"
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
              fullWidth                  
              type="date"
              variant="outlined"
              value={formData.effective_date}
              onChange={(e) => handleInputChange("effective_date", e.target.value)}
            />
          </Grid>  
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => {
            setDialogOpen(false);
            setSelectedRoles(undefined);
            setRateType("fixed");
            resetForm();
          }}
          sx={{
            color: reduxTheme.primary_colors[400],
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: reduxTheme.primary_colors[400]
          }}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          onClick={handleEditRate}
          disabled={formLoading}
          sx={{ bgcolor: reduxTheme.primary_colors[400] }}
        >
          {formLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}Update Rate Card
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRateDialog;
